import * as TabsPrimitive from '@radix-ui/react-tabs';
import type { ReactNode } from 'react';
import { cx } from './cx.ts';

/**
 * Segmented-control tabs (Radix). For deep-linkable page-level tabs
 * keep using routes; this component is for local view switching
 * (time ranges, sub-panels).
 */
interface TabsProps {
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly children: ReactNode;
  readonly className?: string;
}

export function Tabs({ value, onValueChange, children, className }: TabsProps): JSX.Element {
  return (
    <TabsPrimitive.Root value={value} onValueChange={onValueChange} className={className}>
      {children}
    </TabsPrimitive.Root>
  );
}

interface TabsListProps {
  readonly children: ReactNode;
  readonly ariaLabel?: string;
  readonly className?: string;
}

export function TabsList({ children, ariaLabel, className }: TabsListProps): JSX.Element {
  return (
    <TabsPrimitive.List
      aria-label={ariaLabel}
      className={cx(
        'inline-flex gap-0.5 rounded-lg border border-line-soft bg-canvas p-0.5',
        className,
      )}
    >
      {children}
    </TabsPrimitive.List>
  );
}

interface TabsTriggerProps {
  readonly value: string;
  readonly children: ReactNode;
  readonly testId?: string;
}

export function TabsTrigger({ value, children, testId }: TabsTriggerProps): JSX.Element {
  return (
    <TabsPrimitive.Trigger
      value={value}
      data-testid={testId}
      className="rounded px-3 py-1.5 text-dense font-medium text-paper-low transition-colors duration-120 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 data-[state=active]:bg-raised data-[state=active]:text-paper"
    >
      {children}
    </TabsPrimitive.Trigger>
  );
}

interface TabsContentProps {
  readonly value: string;
  readonly children: ReactNode;
  readonly className?: string;
}

export function TabsContent({ value, children, className }: TabsContentProps): JSX.Element {
  return (
    <TabsPrimitive.Content value={value} className={cx('focus-visible:outline-none', className)}>
      {children}
    </TabsPrimitive.Content>
  );
}
